import { Button, CheckboxV2, Modal, Spinner, Typography } from '@ag1/ui';
import { SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID } from '@portal/config/base';
import { PromotionalBanner } from '@portal/processes/SubscriptionPageV2/hooks/subscriptions.types';
import { SubscriptionsResponseV2 } from '@portal/state/internal/getSubscriptions/types';
import { useQueryGetShopifyCollectionProducts } from 'processes/SubscriptionPageV2/hooks/productsByCollections';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { showNotification } from 'shared/ui/Notification';
import {
  findSubscriptionByContractId,
  getLineItemIdByVariantId,
  getShopifyContractId
} from 'shared/utils/subscriptionV2Helpers';
import { useDoubleSubscriptionRequestPromiseV2 } from 'state/hooks/useDoubleSubscriptionRequestPromiseV2';
import { useStoreValue } from 'state/hooks/useStore';
import { SubscriptionDetailsV2Response } from 'state/internal/getSubscriptionDetails/types';
import ActiveSubscriptionCardV2 from './ActiveSubscriptionCardV2';
import style from './DoubleSubscriptionPromotionalModalV2.module.scss';

interface DoubleSubscriptionPromotionalModalV2Props {
  isOpen: boolean;
  switchableSubscriptions: SubscriptionsResponseV2;
  bannerData: PromotionalBanner | undefined;
  onClose: () => void;
}

type PromiseAnswerType = {
  contractId: string | number;
  subscriptionName: string;
};

const DoubleSubscriptionPromotionalModalV2 = ({
  isOpen,
  switchableSubscriptions,
  bannerData,
  onClose
}: DoubleSubscriptionPromotionalModalV2Props) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { isSubmitting }
  } = useForm({
    mode: 'onChange'
  });

  const store = useStoreValue();
  const { t } = useTranslation('modalWindows');
  const CONTENT = t('addProductFromSlider', { returnObjects: true });
  const { isPending: isUpdatingSubscription, doubleSubscriptionRequestPromiseV2 } =
    useDoubleSubscriptionRequestPromiseV2();

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const {
    isPending: isLoadingProduct,
    isFetching: isFetchingProduct,
    error
  } = useQueryGetShopifyCollectionProducts({
    storeCode: store,
    collectionHandle: 'bab'
  });

  const allSubscriptionsCards = watch();

  const checkedSubscriptionsCount: number = Object.values(allSubscriptionsCards).filter((v) =>
    Boolean(v)
  ).length;

  const checkedContractIds = Object.entries(allSubscriptionsCards)
    .filter(([, isChecked]) => isChecked)
    .map(([contractId]) => contractId);

  const handleCheckedAll = () => {
    const contractIds = Object.keys(getValues());
    const areAllChecked = checkedSubscriptionsCount === switchableSubscriptions.length;

    contractIds.forEach((contractId) => {
      setValue(contractId, !areAllChecked);
    });
  };

  const disableSubmitButton = useMemo(
    () =>
      checkedSubscriptionsCount === 0 ||
      isLoadingProduct ||
      isFetchingProduct ||
      isUpdatingSubscription,
    [checkedSubscriptionsCount, isLoadingProduct, isFetchingProduct, isUpdatingSubscription]
  );

  const onSubmit = () => {
    const singleVariantId = SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store].default;

    const promises = checkedContractIds.map((contractId) => {
      const subscriptionData = findSubscriptionByContractId(
        switchableSubscriptions,
        contractId
      )! satisfies SubscriptionDetailsV2Response;

      const singleVariantLineItemId = getLineItemIdByVariantId(subscriptionData, singleVariantId);

      return doubleSubscriptionRequestPromiseV2({
        contractId,
        singleVariantLineItemId
      });
    });

    Promise.allSettled(promises).then((responses) => {
      // prepare to emit toast message
      const failedRequestResults: string[] = [];
      const successRequestResults: PromiseAnswerType[] = [];

      responses.forEach((response) => {
        if (response.status === 'fulfilled') {
          const subscription = response.value;
          const contractId = getShopifyContractId(subscription?.shopify_contract_id || '');
          successRequestResults.push({
            contractId,
            subscriptionName: subscription?.custom_name || contractId
          });
        } else {
          failedRequestResults.push(response.reason);
        }
      });

      // at least 1 subscription change failed
      if (failedRequestResults.length > 0) {
        showNotification('mutationError');
      }

      // 1 double subscription change and 1 successful
      if (promises.length === 1 && successRequestResults.length === promises.length) {
        showNotification('mutationSuccess', {
          title: t('addProductFromSlider.toastSubscription', {
            name: successRequestResults[0].subscriptionName
          })
        });
      }

      // more than 1 double subscription change, but some failed
      if (
        promises.length > 1 &&
        successRequestResults.length < switchableSubscriptions.length &&
        successRequestResults.length > 1
      ) {
        showNotification('mutationSuccess', {
          title: `${CONTENT.toastSeveralSubscription} ${successRequestResults.length} ${CONTENT.severalSubscriptions}`
        });
      }

      // more than 1 double subscription change, and all successful
      if (promises.length > 1 && successRequestResults.length === switchableSubscriptions.length) {
        showNotification('mutationSuccess', { title: bannerData?.successMessage });
      }

      onClose();
    });
  };

  const handleSubmitInternal = handleSubmit(() => {
    onSubmit();
  });

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>{CONTENT.titleSubscription}</Modal.Header>
      <Modal.Content hasFooter>
        <form onSubmit={handleSubmitInternal} className={style.content}>
          <Typography variant="portalText" size="1" color="black-new" noPadding>
            {CONTENT.text}
          </Typography>

          <CheckboxV2
            id="checkedAllSubscriptions"
            label={CONTENT.checkbox}
            checked={checkedSubscriptionsCount === switchableSubscriptions.length}
            onChange={handleCheckedAll}
          />

          <div className={style.cardList}>
            {switchableSubscriptions.map((subscription) => {
              const contractId = getShopifyContractId(subscription.shopify_contract_id);

              return (
                <Controller
                  key={subscription.custom_name}
                  control={control}
                  name={contractId}
                  defaultValue={false}
                  render={({ field }) => (
                    <ActiveSubscriptionCardV2
                      {...field}
                      subscriptionName={subscription.custom_name || contractId}
                      subscriptionProducts={subscription.subscription_only_items}
                      oneTimeProducts={subscription.next_order_only_items}
                      nextOrderDate={subscription.next_order_date}
                      orderTotal={subscription.total_order_cost}
                      outerChecked={field.value}
                    />
                  )}
                />
              );
            })}
          </div>
        </form>
      </Modal.Content>
      <Modal.Footer>
        <Button
          type="submit"
          theme="new"
          fluid
          size={{ s: 'medium', xxs: 'small' }}
          variant="primary"
          disabled={disableSubmitButton}
          onClick={handleSubmitInternal}
          className={style.btn}>
          {isSubmitting || isUpdatingSubscription ? (
            <Spinner color="brand-green" size={24} withOverlay={false} />
          ) : (
            CONTENT.addProductButton
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DoubleSubscriptionPromotionalModalV2;
